import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
} from 'axios'

import { API_BASE_URL, STATUS_CODES } from '@/utils/constants'
import { ROUTES } from '@/utils/routes'

import { CLERK_JWT_TEMPLATE } from './constants'

declare global {
  interface Window {
    Clerk?: {
      session?: {
        getToken: (options?: { template?: string }) => Promise<string | null>
      }
      signOut: () => Promise<void>
    }
  }
}

/**
 * Centralized Axios Configuration Module
 *
 * This module provides a centralized axios configuration with:
 * - Automatic JWT Bearer token attachment via request interceptor
 * - Global 401 error handling with automatic redirect to login
 * - Recursive retry logic for network errors and 5xx server errors
 * - Promise cancellation support with cancel token
 * - Smart header merging (user Authorization header overwrites default)
 *
 * Key Exports:
 * - `AXIOS_INSTANCE`: Pre-configured axios instance with interceptors
 * - `customInstance<T>()`: Generic request function with retry and cancellation support
 * - `CancellablePromise<T>`: Type for promises with cancel method
 *
 * Usage Guidelines:
 * - Use `AXIOS_INSTANCE` for simple requests without retry logic
 * - Use `customInstance()` for requests requiring retry logic or cancellation
 * - Call `promise.cancel()` to abort in-flight requests
 * - Configure retry behavior via `RETRY_CONFIG` constant
 *
 * Retry Configuration:
 * - Retry count defaults to 0 for easier debugging (modify RETRY_CONFIG.retries)
 * - Retries only on network errors (no response) or 5xx server errors (500-599)
 * - Delay between retries calculated via RETRY_CONFIG.retryDelay function
 * - Retry condition customizable via RETRY_CONFIG.retryCondition function
 *
 * @example
 * // Simple request with AXIOS_INSTANCE
 * const response = await AXIOS_INSTANCE.get('/users')
 *
 * @example
 * // Request with cancellation
 * const promise = customInstance<User>({ method: 'get', url: '/users/123' })
 * // Later, cancel the request
 * promise.cancel()
 */

// ============================================================================
// TypeScript Types and Interfaces
// ============================================================================

/**
 * Promise type with attached cancel method for request cancellation
 */
export type CancellablePromise<T> = Promise<T> & {
  /** Cancel the request with an optional message */
  cancel: (message?: string) => void
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Retry configuration for failed requests
 */
const RETRY_CONFIG = {
  /** Number of retry attempts (default: 0 for easier debugging) */
  retries: 0,
  /**
   * Delay calculation function based on attempt number
   * @param _attemptNumber - Current attempt number (1-based)
   * @returns Delay in milliseconds
   */
  retryDelay: (_attemptNumber: number): number => {
    return 1000 // 1 second delay
  },
  /**
   * Condition to determine if an error should trigger a retry
   * @param error - Axios error object
   * @returns true if request should be retried
   */
  retryCondition: (error: AxiosError): boolean => {
    // Retry on network errors (no response) or 5xx server errors
    return (
      !error.response ||
      (error.response.status >= 500 && error.response.status <= 599)
    )
  },
}

// ============================================================================
// Axios Instance Configuration
// ============================================================================

/**
 * Pre-configured axios instance with base URL, credentials, and interceptors
 *
 * Features:
 * - Base URL from environment variable or default
 * - Credentials enabled for CORS requests
 * - JSON content-type header
 * - Request interceptor for JWT token attachment
 * - Response interceptor for global 401 error handling
 *
 * This instance should be used as the default for all API calls.
 *
 * @example
 * const response = await AXIOS_INSTANCE.get('/users')
 * const user = await AXIOS_INSTANCE.post('/users', userData)
 */
export const AXIOS_INSTANCE = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ============================================================================
// Request Interceptor - JWT Authentication with Smart Header Merging
// ============================================================================

/**
 * Request interceptor that attaches JWT Bearer token from localStorage
 *
 * Behavior:
 * - Checks localStorage for 'token' key
 * - If user provides Authorization header in config, keeps it (user header takes precedence)
 * - Otherwise, adds Bearer token from localStorage if available
 * - Handles missing token gracefully
 * - Safe for SSR/testing environments (checks localStorage availability)
 *
 * Smart Header Merging:
 * - User-provided Authorization header overwrites interceptor default
 * - All other headers are merged normally
 */
const getAuthToken = async (): Promise<string | null> => {
  if (typeof localStorage === 'undefined') {
    return null
  }

  // Try getting the session token from Clerk directly if it exists on the window
  const gThis = globalThis
  if (gThis.window?.Clerk?.session) {
    const clerkToken = await gThis.window.Clerk.session.getToken({
      template: CLERK_JWT_TEMPLATE,
    })
    if (clerkToken) {
      return clerkToken
    }
  }

  // Fallback to legacy token in local storage if not available
  return localStorage.getItem('token')
}

AXIOS_INSTANCE.interceptors.request.use(
  async (config) => {
    // Only add default token if Authorization header is not already set
    if (!config.headers.Authorization) {
      const token = await getAuthToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// ============================================================================
// Response Interceptor - Global Authentication Error Handling
// ============================================================================

/**
 * Response interceptor for global authentication and authorization error handling
 *
 * Handles authentication and authorization errors:
 * - 401 Unauthorized: Invalid or expired token
 * - 403 Forbidden: Valid token but insufficient permissions
 *
 * Actions:
 * - Removes invalid token from localStorage
 * - Redirects to login page
 * - Prevents redirect loop on login endpoint failures
 *
 * This replaces the response interceptor previously in authApi.ts
 */
let isRedirectingToLogin = false

const isBypassEndpoint = (url?: string) => {
  if (!url) return false
  return (
    url.includes('/auth/login') ||
    url.includes('/v1/login') ||
    url.includes('/auth/logout') ||
    url.includes('blob.core.windows.net')
  )
}

const handleUnauthorizedError = () => {
  if (!isRedirectingToLogin) {
    isRedirectingToLogin = true

    // 1. Remove invalid token from local storage
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('token')
    }

    // 2. Dispatch a custom event to let the React tree handle Clerk logout and React Router navigation.
    // This avoids missing window.Clerk and race conditions with window.location.href.
    globalThis.dispatchEvent(new Event('auth:logout'))
  }
}

const saveFailedRequestContext = (config?: AxiosRequestConfig) => {
  if (config && typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(
      'failedRequestContext',
      JSON.stringify({
        url: config.url,
        method: config.method,
        data: config.data,
        params: config.params,
        sourceUrl: globalThis.location.pathname,
      })
    )
  }
}

AXIOS_INSTANCE.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status
    const url = error.config?.url

    if (isBypassEndpoint(url)) {
      return Promise.reject(error) //NOSONAR
    }

    if (status === STATUS_CODES.UNAUTHORIZED) {
      handleUnauthorizedError()
      return Promise.reject(error) //NOSONAR
    }

    // Background endpoints only bypass 403, 404, and 500 redirects
    if (url?.includes('/negotiate')) {
      return Promise.reject(error) //NOSONAR
    }

    const currentPath = globalThis.location.pathname

    switch (status) {
      case STATUS_CODES.FORBIDDEN:
        if (currentPath !== ROUTES.UNAUTHORIZED) {
          globalThis.location.href = ROUTES.UNAUTHORIZED
        }
        break
      case STATUS_CODES.NOT_FOUND:
        if (currentPath !== ROUTES.NOT_FOUND) {
          globalThis.location.href = ROUTES.NOT_FOUND
        }
        break
      case STATUS_CODES.INTERNAL_SERVER_ERROR:
        if (currentPath !== ROUTES.INTERNAL_SERVER_ERROR) {
          saveFailedRequestContext(error.config)
          globalThis.location.href = ROUTES.INTERNAL_SERVER_ERROR
        }
        break
    }

    return Promise.reject(error) //NOSONAR
  }
)

// ============================================================================
// Retry Logic with Recursive Pattern
// ============================================================================

/**
 * Manual retry function using recursive pattern
 *
 * This pattern is preferred over axios-retry for custom instance configurations
 * as it provides better control over the retry logic and works seamlessly with
 * our cancellation token approach.
 *
 * @param requestFn - Function that executes the axios request
 * @param retryCount - Number of remaining retry attempts
 * @returns Promise resolving to AxiosResponse of type T
 * @throws Error if all retry attempts are exhausted
 */
const executeWithRetry = async <T>(
  requestFn: () => Promise<AxiosResponse<T>>,
  retryCount = RETRY_CONFIG.retries
): Promise<AxiosResponse<T>> => {
  try {
    return await requestFn()
  } catch (error) {
    if (retryCount > 0 && RETRY_CONFIG.retryCondition(error as AxiosError)) {
      // Calculate current attempt number for delay calculation
      const attemptNumber = RETRY_CONFIG.retries - retryCount + 1

      // Wait before retrying
      await new Promise((resolve) =>
        setTimeout(resolve, RETRY_CONFIG.retryDelay(attemptNumber))
      )

      // Recursive retry with decremented count
      return executeWithRetry(requestFn, retryCount - 1)
    }

    throw error
  }
}

// ============================================================================
// Custom Instance with Retry and Cancellation
// ============================================================================

/**
 * Generic axios request function with retry logic and cancellation support
 *
 * Features:
 * - Generic type parameter for type-safe responses
 * - Automatic cancel token creation
 * - Configuration merging (optional config overrides base config)
 * - Recursive retry logic with exponential backoff support
 * - Promise extension pattern for direct cancellation
 *
 * @template T - Response data type
 * @param config - Base axios request configuration
 * @param options - Optional config to merge/override base config
 * @returns CancellablePromise<T> with attached cancel() method
 *
 * @example
 * // Basic request
 * const promise = customInstance<User>({ method: 'get', url: '/users/123' })
 * const user = await promise
 *
 * @example
 * // Cancel a request
 * const promise = customInstance<User>({ method: 'get', url: '/users/123' })
 * promise.cancel() // Abort the request
 */
export function customInstance<T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): CancellablePromise<T> {
  // Create cancel token source
  const source: CancelTokenSource = axios.CancelToken.source()

  // Merge configurations (optional config overrides base config)
  const finalConfig: AxiosRequestConfig = {
    ...config,
    ...options,
    cancelToken: source.token,
  }

  // Create request function for retry logic
  const requestFn = () => AXIOS_INSTANCE.request<T>(finalConfig)

  // Execute request with retry logic and extract data from response
  const promise = executeWithRetry<T>(requestFn).then(
    (response) => response.data
  ) as CancellablePromise<T>

  // Attach cancel method to promise (promise extension pattern)
  promise.cancel = (message?: string) => {
    source.cancel(message || 'Query was cancelled')
  }

  return promise
}

export type ErrorType<Error> = AxiosError<Error>

export type BodyType<BodyData> = BodyData
